import React, { useState, useEffect, useCallback } from 'react';
import AuthModal from './components/AuthModal';
import ProgressBar from './components/ProgressBar';
import ScraperForm from './components/ScraperForm';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function App() {
  const [location, setLocation] = useState('');
  const [format, setFormat] = useState('csv');
  const [scrolls, setScrolls] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authPassword, setAuthPassword] = useState('');

  // Auto-open modal on first load if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setIsModalOpen(true);
    }
  }, []);

  const handleAuthSuccess = (password) => {
    setAuthPassword(password);
    setIsAuthenticated(true);
  };

  const handleSignInClick = () => {
    setIsModalOpen(true);
  };

  const connectEventSource = useCallback(() => {
    let eventSource;
    let retryCount = 0;
    const maxRetries = 3;

    const connect = () => {
      if (retryCount >= maxRetries) {
        setError('Failed to establish progress connection. Please try again.');
        setLoading(false);
        return;
      }

      console.log('Connecting to EventSource...');
      if (eventSource) {
        eventSource.close();
      }

      try {
        eventSource = new EventSource(`${API_BASE_URL}/progress`, {
          withCredentials: false
        });

        eventSource.onopen = (event) => {
          console.log('EventSource onopen triggered', event);
          retryCount = 0;
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (typeof data.progress === 'number') {
              setProgress(data.progress);
            }
            if (data.status) {
              setStatus(data.status);
            }
            if (data.status === 'Scraping completed successfully' || data.progress === 100) {
              eventSource.close();
            }
          } catch (error) {
            console.error('Error parsing progress data:', error);
          }
        };

        eventSource.onerror = (error) => {
          console.error('EventSource error:', error);
          console.log('EventSource readyState on error:', eventSource.readyState);
          eventSource.close();
          retryCount++;
          
          if (loading && retryCount < maxRetries) {
            console.log(`Retrying connection (${retryCount}/${maxRetries})...`);
            setTimeout(connect, 2000 * retryCount);
          } else if (retryCount >= maxRetries) {
            setError('Connection lost. Please try again.');
            setLoading(false);
          }
        };
      } catch (error) {
        console.error('Error creating EventSource:', error);
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(connect, 2000 * retryCount);
        } else {
          setError('Failed to establish connection. Please try again.');
          setLoading(false);
        }
      }
    };

    const cleanup = connect();

    return () => {
      if (eventSource) {
        console.log('Cleaning up EventSource connection');
        eventSource.close();
      }
      if (cleanup) cleanup();
    };
  }, [loading]);

  useEffect(() => {
    let cleanup;
    if (loading) {
      cleanup = connectEventSource();
    }
    return cleanup;
  }, [loading, connectEventSource]);

  const resetForm = () => {
    setLocation('');
    setQuery('');
    setCategory('');
    setFormat('csv');
    setScrolls(0);
    setProgress(0);
    setStatus('');
    setLoading(false);
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated || !authPassword) {
      setError('Please sign in first');
      setIsModalOpen(true);
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);
    setStatus('Initializing...');

    try {
      const baseUrl = process.env.NODE_ENV === 'development' ? '' : API_BASE_URL;
      const response = await fetch(
        `${baseUrl}/scrape?search_query=${encodeURIComponent(query)}&format=${format}&scrolls=${scrolls}&location=${encodeURIComponent(location)}&category=${encodeURIComponent(category)}&password=${encodeURIComponent(authPassword)}`
      );

      if (response.status === 401) {
        setIsAuthenticated(false);
        setAuthPassword('');
        setError('Session expired. Please sign in again.');
        setIsModalOpen(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch data');
      }
      
      setLoading(false);
      
      if (format === 'csv') {
        const timestamp = new Date().toISOString().replace(/:/g, '-');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `businesses-${query}-${timestamp}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        console.log('Received data:', data);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [query, format, scrolls, location, category, isAuthenticated, authPassword]);

  useEffect(() => {
    if (progress === 100) {
      resetForm();
    }
  }, [progress]);

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '20px' }}>
      <AuthModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Business Scraper</h1>
        {isAuthenticated && (
          <div style={{ fontSize: '14px', color: '#4CAF50', fontWeight: '500' }}>
            ✓ Authenticated
          </div>
        )}
      </div>
      
      <ProgressBar 
        progress={progress}
        status={status}
        loading={loading}
      />
      
      <ScraperForm 
        onSubmit={handleSubmit}
        location={location}
        setLocation={setLocation}
        query={query}
        setQuery={setQuery}
        category={category}
        setCategory={setCategory}
        format={format}
        setFormat={setFormat}
        scrolls={scrolls}
        setScrolls={setScrolls}
        loading={loading}
        isAuthenticated={isAuthenticated}
        onSignInClick={handleSignInClick}
      />
      
      {error && (
        <div style={{ 
          color: '#d32f2f', 
          backgroundColor: '#ffebee',
          padding: '15px',
          borderRadius: '4px',
          marginTop: '20px',
          border: '1px solid #f5c6cb'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}

export default App;
